import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SiteNavbar from '@/components/feature/SiteNavbar';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollToTop from '@/components/feature/ScrollToTop';
import RevealWrapper from '@/pages/home/components/RevealWrapper';
import defterKasaLogo from '@/pages/images/defter-kasa-logo.png';

const SECTION_IDS = ['01', '02', '03', '04', '05', '06'] as const;

type SectionId = typeof SECTION_IDS[number];

interface SectionItem {
  id: SectionId;
  isList: boolean;
  itemCount?: number;
}

const SECTIONS: SectionItem[] = [
  { id: '01', isList: false },
  { id: '02', isList: true, itemCount: 3 },
  { id: '03', isList: true, itemCount: 3 },
  { id: '04', isList: true, itemCount: 2 },
  { id: '05', isList: true, itemCount: 2 },
  { id: '06', isList: true, itemCount: 3 },
];

export default function DefterKasaPrivacyPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const summaryItems = [
    t('dk.privacy.summary.0'),
    t('dk.privacy.summary.1'),
    t('dk.privacy.summary.2'),
    t('dk.privacy.summary.3'),
  ];

  const navLinks = [
    { label: 'nav.home', href: '/', isRoute: true },
    { label: 'dk.nav.features', href: '/defter-kasa', isRoute: true },
  ];

  return (
    <div className="min-h-screen bg-[#0c0c0c]">
      <SiteNavbar links={navLinks} sectionAware={false} />

      <main className="pt-16">
        {/* Hero */}
        <section className="relative bg-[#0c0c0c] border-b border-white/5 overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none opacity-20"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
              backgroundSize: '80px 80px',
            }}
          />
          <div className="px-6 lg:px-14 py-16 lg:py-24 relative z-10">
            <RevealWrapper direction="up" duration={500}>
              <div className="flex items-center gap-3 mb-10">
                <button
                  onClick={() => navigate('/defter-kasa')}
                  className="font-mono text-xs text-neutral-600 hover:text-neutral-400 tracking-widest uppercase transition-colors duration-200 flex items-center gap-2 cursor-pointer"
                >
                  <i className="ri-arrow-left-line" />
                  {t('dk.privacy.back')}
                </button>
                <span className="text-neutral-800">/</span>
                <span className="font-mono text-xs text-neutral-600 tracking-widest uppercase">
                  {t('dk.privacy.breadcrumb.products')}
                </span>
                <span className="text-neutral-800">/</span>
                <button
                  onClick={() => navigate('/defter-kasa')}
                  className="font-mono text-xs text-neutral-600 hover:text-neutral-400 tracking-widest uppercase transition-colors duration-200 cursor-pointer"
                >
                  Defter Kasa
                </button>
                <span className="text-neutral-800">/</span>
                <span className="font-mono text-xs text-white tracking-widest uppercase">
                  {t('dk.privacy.breadcrumb.privacy')}
                </span>
              </div>
            </RevealWrapper>

            <RevealWrapper direction="left" duration={700}>
              <div className="max-w-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 border border-white/10 flex items-center justify-center bg-[#161616]">
                    <img
                      src={defterKasaLogo}
                      alt="Defter Kasa"
                      className="w-7 h-7 object-contain"
                    />
                  </div>
                  <span className="font-mono text-xs text-neutral-600 tracking-widest uppercase">
                    Defter Kasa
                  </span>
                </div>
                <h1 className="font-black text-white text-5xl lg:text-6xl tracking-tight leading-none mb-6">
                  {t('dk.privacy.title')}
                </h1>
                <p className="text-neutral-500 text-sm leading-relaxed mb-6 max-w-xl">
                  {t('dk.privacy.intro')}
                </p>
                <span className="font-mono text-xs text-neutral-700 tracking-widest">
                  {t('dk.privacy.lastUpdated')}
                </span>
              </div>
            </RevealWrapper>
          </div>
        </section>

        {/* Summary bar */}
        <RevealWrapper direction="up" threshold={0.1}>
          <div className="border-b border-white/5 bg-[#111111]">
            <div className="px-6 lg:px-14 py-5 flex flex-wrap items-center gap-6">
              <span className="font-mono text-[10px] text-neutral-700 tracking-widest uppercase">
                {t('dk.privacy.summary.title')}
              </span>
              {summaryItems.map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-xs text-neutral-400" />
                  </div>
                  <span className="font-mono text-xs text-neutral-500 tracking-wider">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </RevealWrapper>

        {/* Policy Cards Grid */}
        <section className="bg-[#0c0c0c]">
          <div className="px-6 lg:px-14 py-16 lg:py-20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
              {SECTIONS.map((section, i) => (
                <RevealWrapper key={section.id} direction="up" delay={i * 60} threshold={0.05}>
                  <div className="bg-[#0c0c0c] p-8 hover:bg-white/[0.02] transition-colors duration-200 h-full">
                    <h2 className="font-black text-white text-base tracking-tight mb-4">
                      {t(`dk.privacy.s${section.id}.title`)}
                    </h2>
                    {section.isList ? (
                      <ul className="space-y-3">
                        {Array.from({ length: section.itemCount ?? 0 }, (_, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <span className="mt-1.5 w-1 h-1 rounded-full bg-neutral-600 shrink-0" />
                            <span className="text-neutral-500 text-sm leading-relaxed">
                              {t(`dk.privacy.s${section.id}.item${idx}`)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-neutral-500 text-sm leading-relaxed">
                        {t(`dk.privacy.s${section.id}.content`)}
                      </p>
                    )}
                  </div>
                </RevealWrapper>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <RevealWrapper direction="up" threshold={0.1}>
          <section className="bg-[#111111] border-t border-white/5">
            <div className="px-6 lg:px-14 py-12">
              <p className="font-mono text-[10px] text-neutral-700 tracking-widest uppercase mb-4">
                {t('dk.privacy.cta.question')}
              </p>
              <p className="text-neutral-400 text-sm mb-8 max-w-md">
                {t('dk.privacy.cta.desc')}
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="mailto:info@g-software.org"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/15 text-neutral-300 font-mono text-xs tracking-widest hover:border-white/40 hover:text-white transition-all duration-200 sm:whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line" />
                  {t('dk.privacy.cta.email')}
                </a>
                <a
                  href="https://github.com/altusparati"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/15 text-neutral-300 font-mono text-xs tracking-widest hover:border-white/40 hover:text-white transition-all duration-200 sm:whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-github-line" />
                  {t('dk.privacy.cta.github')}
                </a>
              </div>
            </div>
          </section>
        </RevealWrapper>
      </main>

      <SiteFooter />
      <ScrollToTop />
    </div>
  );
}

